import { useEffect, useState, useRef } from "react";
import { Search, Plus, Package, FileText, Edit2, Trash2, AlertTriangle, ChevronLeft, ChevronRight, Layers, Upload, Download, Image } from "lucide-react";
import ProductForm from "../components/ProductForm";
import api from "../services/api";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";

export default function Products() {
  const { user } = useAuth();
  const isDiner = user?.tipo_negocio === "comedor";
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  const [stockTotal, setStockTotal] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);

  const [activeCategory, setActiveCategory] = useState("todas");

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchProducts = async (page = 1, searchTerm = search, category = activeCategory) => {
    try {
      setLoading(true);
      const categoryParam = category !== "todas" ? `&categoria=${category}` : "";
      const res = await api.get(`/products/paginated?page=${page}&search=${searchTerm}${categoryParam}`);

      if (res.data.data) {
        setProducts(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);

        setStockTotal(res.data.stock_total || 0);
        setLowStockCount(res.data.stock_bajo || 0);
      } else {
        setProducts(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Could not load products", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchProducts(newPage, search, activeCategory);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, value, activeCategory);
    }, 500);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    fetchProducts(1, search, cat);
  };

  const handleDelete = async (product) => {
    const typeLabel = product.tipo_item === 2 ? "service" : "product";

    const res = await Swal.fire({
      title: `Delete ${typeLabel}?`,
      text: `This will delete: ${product.nombre}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (res.isConfirmed) {
      try {
        await api.delete(`/products/${product.id}`);
        Swal.fire("Deleted", `${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} deleted successfully`, "success");
        fetchProducts(currentPage, search, activeCategory);
      } catch (error) {
        Swal.fire("Error", `Could not delete the ${typeLabel}`, "error");
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/products/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "products_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire("Error", "Could not download the template", "error");
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      Swal.fire("Attention", "Select a file first", "warning");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await api.post("/products/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
      fetchProducts(1, "");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Error importing the file", "error");
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const showLowStockProducts = async () => {
    try {
      const res = await api.get("/products/stock-bajo");
      const lowStockProducts = res.data;

      if (lowStockProducts.length === 0) {
        Swal.fire({
          icon: "success",
          title: "✅ All good",
          text: "No products with low stock",
          confirmButtonText: "Got it",
        });
        return;
      }

      const itemsHTML = lowStockProducts
        .map((p) => {
          const isLow = p.stock <= (p.stock_minimo || 5);
          return `
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: ${isLow ? "#fff5f5" : "#f7fafc"};
              border-radius: 8px;
              margin-bottom: 8px;
              border: 2px solid ${isLow ? "#fc8181" : "#e2e8f0"};
            ">
              <div style="text-align: left; flex: 1;">
                <div style="font-weight: 600; color: #2d3748; font-size: 15px;">
                  ${p.nombre}
                </div>
                <div style="font-size: 12px; color: #718096; margin-top: 2px;">
                  Code: ${p.codigo || "N/A"} | Minimum: ${p.stock_minimo || 5}
                </div>
              </div>
              <div style="text-align: right;">
                <div style="font-weight: 700; color: #ed8936; font-size: 18px;">
                  ⚠️ ${p.stock}
                </div>
                <div style="font-size: 11px; color: #718096;">
                  units
                </div>
              </div>
            </div>
          `;
        })
        .join("");

      Swal.fire({
        title: `⚠️ Low Stock Products (${lowStockProducts.length})`,
        html: `
          <div style="text-align: left; max-height: 400px; overflow-y: auto;">
            <p style="color: #718096; font-size: 14px; margin-bottom: 16px;">
              These products need restocking:
            </p>
            ${itemsHTML}
          </div>
        `,
        confirmButtonText: "Close",
        width: "600px",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } catch (error) {
      console.error("Error loading low stock products:", error);
      Swal.fire("Error", "Could not load products", "error");
    }
  };

  const showPresentations = async (product) => {
    try {
      const res = await api.get(`/products/${product.id}`);
      const productData = res.data;

      if (!productData.presentations || productData.presentations.length === 0) {
        Swal.fire("No presentations", "This product has no presentations configured.", "info");
        return;
      }

      const presentationsHTML = productData.presentations
        .map((pres) => {
          const typeLabel = pres.tipo_presentacion === "variante" ? "Variant" : "Pack";
          const badgeColor = pres.tipo_presentacion === "variante" ? "#805ad5" : "#38b2ac";

          let stockHTML = "";
          if (pres.tipo_presentacion === "variante") {
            const stock = pres.stock_independiente || 0;
            const isLow = stock <= 5;
            stockHTML = `
              <div style="font-size: 12px; color: ${isLow ? "#ed8936" : "#718096"}; margin-top: 4px;">
                ${isLow ? "⚠️" : "📦"} Stock: <strong>${stock}</strong>
              </div>
            `;
          } else {
            stockHTML = `
              <div style="font-size: 12px; color: #718096; margin-top: 4px;">
                📦 Factor: ${pres.factor_conversion} unit${pres.factor_conversion > 1 ? "s" : ""}
              </div>
            `;
          }

          const costHTML =
            pres.tipo_presentacion === "variante" && pres.precio_compra
              ? `<div style="font-size: 13px; color: #718096; margin-top: 2px;">Cost: $${Number(pres.precio_compra).toFixed(2)}</div>`
              : "";

          return `
            <div style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 12px 16px;
              background: #f7fafc;
              border-radius: 8px;
              margin-bottom: 8px;
              border: 2px solid #e2e8f0;
            ">
              <div style="text-align: left; flex: 1;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <div style="font-weight: 600; color: #2d3748; font-size: 15px;">
                    ${pres.descripcion}
                  </div>
                  <span style="
                    font-size: 10px;
                    padding: 2px 6px;
                    background: ${badgeColor};
                    color: white;
                    border-radius: 4px;
                    font-weight: 600;
                  ">
                    ${typeLabel}
                  </span>
                </div>
                ${stockHTML}
                ${costHTML}
              </div>
              <div style="text-align: right;">
                <div style="font-weight: 700; color: #48bb78; font-size: 18px;">
                  $${Number(pres.precio_venta).toFixed(2)}
                </div>
                <div style="font-size: 11px; color: #718096;">
                  Sale Price
                </div>
              </div>
            </div>
          `;
        })
        .join("");

      Swal.fire({
        title: `📦 ${product.nombre}`,
        html: `
          <div style="text-align: left; max-height: 400px; overflow-y: auto;">
            <p style="color: #718096; font-size: 14px; margin-bottom: 16px;">
              Available presentations:
            </p>
            ${presentationsHTML}
          </div>
        `,
        confirmButtonText: "Close",
        width: "600px",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } catch (error) {
      console.error("Error loading presentations:", error);
      Swal.fire("Error", "Could not load presentations", "error");
    }
  };

  const renderStock = (product) => {
    if (product.tipo_item === 2) {
      return <span style={{ color: "#a0aec0" }}>-</span>;
    }

    if (product.tiene_presentaciones_variante) {
      return <span style={{ color: "#a0aec0" }}>-</span>;
    }

    const isLow = product.stock <= (product.stock_minimo || 5);

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontWeight: 600,
          color: isLow ? "#ed8936" : "#2d3748",
        }}
      >
        {isLow && <AlertTriangle size={16} color="#ed8936" />}
        {product.stock}
      </span>
    );
  };

  const renderPrice = (product) => {
    const price = Number(product.precio_venta || 0);

    if (price === 0) {
      return (
        <button
          onClick={() => showPresentations(product)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "13px",
            padding: "6px 12px",
            background: "#e6fffa",
            color: "#38b2ac",
            border: "2px solid #38b2ac",
            borderRadius: "6px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#38b2ac";
            e.target.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#e6fffa";
            e.target.style.color = "#38b2ac";
          }}
          title="View pricing for each presentation"
        >
          <Layers size={14} />
          View presentations
        </button>
      );
    }

    return <span style={{ fontWeight: 700, color: "#48bb78" }}>${price.toFixed(2)}</span>;
  };

  const categoryBadge = (cat) => {
    const config = {
      comida: { bg: "#fff7ed", color: "#c2410c", label: "🍽️ Food" },
      bebida: { bg: "#eff6ff", color: "#1d4ed8", label: "🥤 Drink" },
      postre: { bg: "#fdf4ff", color: "#7e22ce", label: "🍰 Dessert" },
      otro: { bg: "#f7fafc", color: "#4a5568", label: "📦 Other" },
      plato: { bg: "#fefce8", color: "#854d0e", label: "🍽️ Entree" },
      pollo: { bg: "#fff7ed", color: "#c2410c", label: "🍗 Chicken" },
      mariscos: { bg: "#ecfdf5", color: "#065f46", label: "🦐 Seafood" },
      carnes: { bg: "#fef2f2", color: "#991b1b", label: "🥩 Meats" },
      sopas: { bg: "#eff6ff", color: "#1e40af", label: "🍜 Soups" },
      especiales: { bg: "#fefce8", color: "#854d0e", label: "⭐ Specials" },
      acompañamientos: { bg: "#f0fdf4", color: "#166534", label: "🍚 Sides" },
    };
    const c = config[cat] || config.otro;
    return (
      <span style={{ fontSize: "11px", padding: "2px 8px", background: c.bg, color: c.color, borderRadius: "4px", fontWeight: 600 }}>
        {c.label}
      </span>
    );
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith("http")) return photoPath;
    return `https://altivawebusa.com/BACK/public/storage/${photoPath}`;
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", overflowX: "hidden" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Package size={28} color="#667eea" />
            <h1 className="card-title">Product/Service Inventory</h1>
          </div>

          {!showForm && (
            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowImportModal(true)}>
                <Upload size={18} style={{ marginRight: 6 }} />
                <span className="d-none d-md-inline">Import Excel</span>
                <span className="d-md-none">Import</span>
              </button>
              <button className="btn btn-success btn-sm" onClick={() => { setEditingProduct(null); setShowForm(true); }}>
                <Plus size={18} />
                <span className="d-none d-md-inline">New Product/Service</span>
                <span className="d-md-none">New</span>
              </button>
            </div>
          )}
        </div>

        <div className="card-body">
          {showForm && (
            <ProductForm
              editingProduct={editingProduct}
              onSaved={() => {
                fetchProducts(currentPage, search, activeCategory);
                setEditingProduct(null);
                setShowForm(false);
              }}
              onCancel={() => {
                setEditingProduct(null);
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
            />
          )}

          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or code..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Searching all products/services...
                </small>
              )}
            </div>
          )}

          {!showForm && (
            <>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", margin: "16px 0" }}>
                {(isDiner
                  ? [
                      { value: "todas", label: "🗂️ All" },
                      { value: "plato", label: "🍽️ Entree" },
                      { value: "pollo", label: "🍗 Chicken" },
                      { value: "mariscos", label: "🦐 Seafood" },
                      { value: "carnes", label: "🥩 Meats" },
                      { value: "sopas", label: "🍜 Soups" },
                      { value: "especiales", label: "⭐ Specials" },
                      { value: "acompañamientos", label: "🍚 Sides" },
                      { value: "bebida", label: "🥤 Drinks" },
                    ]
                  : [
                      { value: "todas", label: "🗂️ All" },
                      { value: "comida", label: "🍽️ Food" },
                      { value: "bebida", label: "🥤 Drinks" },
                      { value: "postre", label: "🍰 Desserts" },
                      { value: "otro", label: "📦 Other" },
                    ]
                ).map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => handleCategoryChange(cat.value)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "20px",
                      border: "2px solid",
                      borderColor: activeCategory === cat.value ? "#667eea" : "#e2e8f0",
                      background: activeCategory === cat.value ? "#667eea" : "white",
                      color: activeCategory === cat.value ? "white" : "#4a5568",
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>Loading products...</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Photo</th>
                        <th className="d-none d-md-table-cell">Code</th>
                        <th>Name</th>
                        <th className="d-none d-md-table-cell">Description</th>
                        <th className="d-none d-md-table-cell">Category</th>
                        <th style={{ textAlign: "right" }}>Price</th>
                        <th style={{ textAlign: "center" }} className="d-none d-md-table-cell">Stock</th>
                        <th style={{ textAlign: "center", width: "180px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr>
                          <td colSpan="8">
                            <div className="text-center" style={{ padding: "40px 20px" }}>
                              <FileText size={48} color="#cbd5e0" style={{ marginBottom: "16px" }} />
                              <p style={{ color: "#a0aec0", fontSize: "16px", margin: 0 }}>
                                {search ? "No products/services found matching your search" : "No products/services registered yet"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        products.map((p) => (
                          <tr key={p.id}>
                            <td>
                              {p.foto ? (
                                <img
                                  src={getPhotoUrl(p.foto)}
                                  alt={p.nombre}
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #e2e8f0",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "8px",
                                    background: "#f7fafc",
                                    border: "1px dashed #cbd5e0",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Package size={20} color="#cbd5e0" />
                                </div>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell">
                              <strong style={{ fontWeight: 600 }}>{p.codigo || "-"}</strong>
                            </td>
                            <td>
                              {p.nombre}
                              {p.tipo_item === 2 && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    background: "#e6fffa",
                                    color: "#38b2ac",
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                  }}
                                >
                                  SERVICE
                                </span>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: "#718096" }}>
                              {p.descripcion || "-"}
                            </td>
                            <td className="d-none d-md-table-cell">{categoryBadge(p.categoria)}</td>
                            <td style={{ textAlign: "right" }}>{renderPrice(p)}</td>
                            <td className="d-none d-md-table-cell" style={{ textAlign: "center" }}>
                              {renderStock(p)}
                            </td>
                            <td>
                              <div className="d-flex gap-2" style={{ justifyContent: "center" }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingProduct(p);
                                    setShowForm(true);
                                  }}
                                  title="Edit product/service"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(p)}
                                  title="Delete product/service"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div style={{ marginTop: "24px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button className="btn btn-secondary btn-sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {products.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Showing {from} - {to} of {total} items
                  </span>
                  <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    <span style={{ color: "#718096", fontSize: "14px" }}>
                      Total Stock (Products): <strong style={{ color: "#667eea" }}>{stockTotal}</strong>
                    </span>
                    <span
                      style={{
                        color: "#718096",
                        fontSize: "14px",
                        cursor: lowStockCount > 0 ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (lowStockCount > 0) {
                          showLowStockProducts();
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (lowStockCount > 0) {
                          e.target.style.textDecoration = "underline";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                      title={lowStockCount > 0 ? "Click to view low stock products" : ""}
                    >
                      Low Stock: <strong style={{ color: "#ed8936" }}>{lowStockCount}</strong>
                      {lowStockCount > 0 && " 👁️"}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showImportModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📥 Import Products from Excel</h5>
                <button className="btn-close" onClick={closeImportModal} />
              </div>

              <div className="modal-body">
                {!importResult ? (
                  <>
                    <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                      <p style={{ margin: 0, fontSize: 13, color: "#166534" }}>
                        <strong>First time?</strong> Download the template with the correct format.
                      </p>
                      <button className="btn btn-sm btn-success mt-2" onClick={downloadTemplate}>
                        <Download size={14} style={{ marginRight: 4 }} /> Download CSV Template
                      </button>
                    </div>

                    <div style={{ background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                      <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#1e40af" }}>File columns:</p>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {["nombre *", "codigo", "precio_venta *", "precio_compra", "stock", "stock_minimo", "descripcion", "is_taxable", "tipo_item"].map((col) => (
                          <span
                            key={col}
                            style={{
                              fontSize: 11,
                              padding: "2px 8px",
                              background: col.includes("*") ? "#dbeafe" : "#e0e7ff",
                              color: "#1e40af",
                              borderRadius: 4,
                              fontWeight: col.includes("*") ? 700 : 400,
                            }}
                          >
                            {col}
                          </span>
                        ))}
                      </div>
                      <p style={{ margin: "8px 0 0", fontSize: 11, color: "#6b7280" }}>
                        * Required | tipo_item: 1=Product, 2=Service | is_taxable: 1=Yes, 0=No
                      </p>
                    </div>

                    <div
                      style={{ border: "2px dashed #d1d5db", borderRadius: 8, padding: 24, textAlign: "center", cursor: "pointer", background: importFile ? "#f0fdf4" : "#fafafa" }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={32} color={importFile ? "#22c55e" : "#9ca3af"} style={{ marginBottom: 8 }} />
                      <p style={{ margin: 0, color: importFile ? "#16a34a" : "#6b7280", fontWeight: importFile ? 600 : 400 }}>
                        {importFile ? `✅ ${importFile.name}` : "Click to select a file"}
                      </p>
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: "#9ca3af" }}>Excel (.xlsx, .xls) or CSV</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        onChange={(e) => setImportFile(e.target.files[0] || null)}
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                      <div style={{ flex: 1, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 12, textAlign: "center" }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>{importResult.importados}</div>
                        <div style={{ fontSize: 12, color: "#166534" }}>Imported</div>
                      </div>
                      <div style={{ flex: 1, background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8, padding: 12, textAlign: "center" }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#2563eb" }}>{importResult.actualizados}</div>
                        <div style={{ fontSize: 12, color: "#1e40af" }}>Updated</div>
                      </div>
                      <div style={{ flex: 1, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 12, textAlign: "center" }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: "#dc2626" }}>{importResult.errores}</div>
                        <div style={{ fontSize: 12, color: "#991b1b" }}>Errors</div>
                      </div>
                    </div>

                    {importResult.errores > 0 && (
                      <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #fca5a5", borderRadius: 8, padding: 8 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: "#dc2626", margin: "0 0 8px" }}>Rows with errors:</p>
                        {importResult.detalle
                          .filter((d) => d.estado === "error")
                          .map((d, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#6b7280", padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
                              <strong>Row {d.fila}:</strong> {d.nombre} — {d.mensaje}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeImportModal}>
                  {importResult ? "Close" : "Cancel"}
                </button>
                {!importResult && (
                  <button className="btn btn-primary" onClick={handleImport} disabled={!importFile || importing}>
                    {importing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Importing...
                      </>
                    ) : (
                      "📥 Import"
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}